import React, { useState } from 'react';
import { APP_URLS } from '../../../consts';
import { Endpoint } from '../../../models/api.model';
import { useSnackbar } from '../../../hooks/snackbar';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading } from '../../../components/shared/Heading';
import { NavigationTemplate } from '../../NavigationTemplate';
import { Spinner } from '../../../components/shared/Spinner';
import { useIframePageGetBySlugQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/iframe-page-get-by-slug.query.generated';
import './IframePage.scss';
import { useUserContext } from '../../../providers/UserProvider';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { useAuth } from 'react-oidc-context';
import placeholderImage from '../../../assets/Games/wheel.png';
import { Paragraph } from '../../../components/shared/Paragraph';
import { Button } from '../../../components/shared/Button';
import { useSignIn } from '../../../hooks/auth';

interface IframePageData {
  title?: string | null;
  src?: string | null;
}

export const IframePage: React.FC = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { slug = '' } = useParams();
  const { addSnack } = useSnackbar();
  const [iframeData, setIframeData] = useState<IframePageData | null>(null);
  const { userProfileId } = useUserContext();
  const auth = useAuth();
  const signIn = useSignIn();

  const handleError = (): void => {
    addSnack({
      type: 'error',
      theme: 'dark',
      message: t('errors.general'),
    });
    navigate(APP_URLS.home);
  };

  const { loading } = useIframePageGetBySlugQuery({
    variables: {
      slug,
    },
    onCompleted: (data) => {
      if (!data.iframe_pages.length) {
        return handleError();
      }

      setIframeData(data.iframe_pages[0]);
    },
    onError: (error) => {
      return handleError();
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  const getIframeSrc = (): string => {
    if (!iframeData || !iframeData.src) return '';

    const url = new URL(iframeData.src);

    if (userProfileId) {
      url.searchParams.append('token', userProfileId);
    }

    return url.toString();
  };

  const onClickLogin = (): void => {
    signIn();
  };

  const onClickRegister = (): void => {
    navigate(APP_URLS.registration);
  };

  return (
    <NavigationTemplate>
      {loading ? (
        <div className="spinner-wrapper">
          <Spinner />
        </div>
      ) : !iframeData ? (
        <div>No iframe data found</div>
      ) : auth.isAuthenticated ? (
        <div className="iframe-page">
          <Heading level={1}>{iframeData.title || 'Untitled'}</Heading>
          <iframe src={getIframeSrc()} title={iframeData.title || 'Game'} />
        </div>
      ) : (
        <div className="error-page">
          <div className="error-page__content">
            <div className="error-page__image-section">
              <img src={placeholderImage} alt="Error" className="iframe-error-page__image" />
            </div>
            <div className="error-page__text-section">
              <Heading level={1} className="error-page__title">
                {iframeData.title}
              </Heading>
              <Paragraph size="md" className="error-page__message">
                Please log in or sign up today to play {iframeData.title}.
              </Paragraph>
              <div>
                <Button
                  onClick={onClickLogin}
                  variant="secondary-alt"
                  size="large"
                  className="iframe-page__cta-btn"
                  type="button"
                >
                  LOGIN
                </Button>
                <Button
                  onClick={onClickRegister}
                  variant="primary"
                  size="large"
                  className="iframe-page__cta-btn"
                  type="button"
                >
                  REGISTER
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </NavigationTemplate>
  );
};
